import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Animated } from 'react-native';

import IconNavRight from '../../../assets/svg/IconNavRight';
import IconRecommend from '../../../assets/svg/IconRecommend';
import IconShopMore from '../../../assets/svg/IconShopMore';
import { neutralDark } from '../../../constants/color';
import { checkItemViewed } from '../../../context/devLogFirehose';
import ScrollController from '../../../elements/ScrollController';
import { useTranslation } from '../../../i18n';
import { navigate } from '../../../utils/navigationService';
import routeUtils from '../../../utils/routes';
import FlatListWithImpression, {
  FlatListWithImpressionType,
} from '../../FlatListWithImpression';
import { itemProducer } from '../../FlatListWithImpression/utils';
import ListItem from '../../ListItem';
import { useData } from './hooks';
import S, { ITEM_MARGIN, ITEM_WIDTH, styles } from './styles';

const LAYOUT_NAME = 'homepage_rated_item';
const keyExtractor = item => `${item}`;

export interface TopRatedRefMethods {
  setIsVisible: (isVisible: boolean) => void;
}

const TopRatedList = forwardRef((_props, ref) => {
  const { data, sources, isFetching } = useData();
  const [isVisible, setIsVisible] = useState(false);

  const [scrollOffset] = useState(() => new Animated.Value(0));
  const impressionList = useRef<FlatListWithImpressionType>(null);

  useImperativeHandle(ref, () => ({
    setIsVisible,
  }));

  const { t } = useTranslation();

  const onPressMoreTopRated = useCallback(
    () => navigate('TopRatedListPage'),
    []
  );

  const ListFooter = memo(() => {
    return (
      <S.WatchMoreContainer
        accessibilityRole="button"
        onPress={onPressMoreTopRated}
      >
        <S.WatchMoreTextSection>
          <IconShopMore />
          <S.WatchMoreText>{t('watch-more')}</S.WatchMoreText>
        </S.WatchMoreTextSection>
      </S.WatchMoreContainer>
    );
  });

  const renderItem = useCallback(
    ({ item, index }: { item: string; index: number }) => {
      return (
        <ListItem
          index={index}
          key={item}
          id={item}
          numColumns={1}
          showRibbon={false}
          showLineThroughPrice={false}
          showLoading={`${item}`.includes('blank')}
          itemWidth={ITEM_WIDTH}
          itemMargin={ITEM_MARGIN}
          contentWrapperStyle={styles.contentWrapperStyle}
          titleStyle={styles.ListItemFontStyle}
          priceCurrencyStyle={styles.ListItemFontStyle}
          priceTildeStyle={styles.ListItemFontStyle}
          priceStyle={styles.ListItemFontStyle}
          wrapperStyle={styles.listItemWrapperStyle}
          layoutName={LAYOUT_NAME}
          showTitlesAndPrice
          showPrice={false}
          source={sources.get(item)}
          shouldDisplayImpressionForDebug={checkItemViewed(
            impressionList?.current?.accumulatedItems,
            item
          )}
          isShowItemLabel={false}
          linesOfTitle={1}
          showRating
          showRatingText
          ratingStarSize={10}
          showSalesAmount={false}
        />
      );
    },
    [sources]
  );

  if (!data?.length) {
    return null;
  }

  return (
    <S.HorizontalContainer>
      <S.HorizontalHeaderWrapper>
        <S.HorizontalHeaderLeft>
          <IconRecommend fillColor={neutralDark} size={18} />
          <S.HorizontalHeaderText>{t('top-rated-desc')}</S.HorizontalHeaderText>
        </S.HorizontalHeaderLeft>
        {!isFetching && (
          <S.MoreTopRated
            accessibilityRole="link"
            href={routeUtils.compileToPath('TopRatedListPage')}
            onPress={onPressMoreTopRated}
            hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
          >
            <S.MoreTopRatedText>{t('watch-more')}</S.MoreTopRatedText>
            <IconNavRight size={14} fillColor={neutralDark} />
          </S.MoreTopRated>
        )}
      </S.HorizontalHeaderWrapper>

      <FlatListWithImpression
        ref={impressionList}
        contentContainerStyle={styles.horizontalListContainer}
        data={data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        horizontal
        showsHorizontalScrollIndicator={false}
        layoutName={LAYOUT_NAME}
        isVisible={isVisible}
        sources={sources}
        dataProducer={itemProducer}
        ListFooterComponent={ListFooter}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollOffset } } }],
          {
            useNativeDriver: false,
          }
        )}
      />
      <ScrollController
        scrollRef={impressionList}
        scrollOffset={scrollOffset}
        dataLength={data?.length || 0}
      />
    </S.HorizontalContainer>
  );
});

export default TopRatedList;
